import { useEffect, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { buildHamPermissionChecksResult } from './buildHamPermissionChecksResult.tsx';
import { computeShouldShowItam } from './computeShouldShowItam.tsx';
import { fetchItamHamWorkspacePermissions } from './fetchItamHamWorkspacePermissions.tsx';
import type { HamPermissionChecksResult } from './ItamPermissionsContext.tsx';
import type { PermissionCheckResult } from './PermissionCheckResult.tsx';

type UseShowItamInput = {
	readonly assetsVerticalWorkspaceAri: string | null;
	readonly staticResults: ReadonlyArray<PermissionCheckResult>;
	readonly verticalInstantiationStatus?: string | null;
};

type UseShowItamResult = {
	readonly showItam: boolean;
	readonly showHardwareSettings: boolean;
};

type HamPermissionResultsState = {
	readonly cloudId: string;
	readonly assetsVerticalWorkspaceAri: string;
	readonly results: ReadonlyArray<PermissionCheckResult>;
};

/**
 * Whether the ITAM navigation entry should be shown.
 *
 * Step 1 (Assets Vertical tenant context + the cloudId-derived static checks) is
 * preloaded alongside the sidebar's `L1MenuItemsQuery` and passed in from the
 * `ConfigurableMenuItems` fragment, so it rides along with the nav query.
 * Step 2 (the workspace-scoped HAM checks) depends on the workspace ARI resolved
 * in step 1, so it is fetched client-side once that ARI is known.
 *
 * The step-1 fields are gated on `itam_ftw` via `@include`, so callers pass a
 * null workspace ARI and empty static results when ITAM is disabled.
 */
export const useShowItam = ({
	assetsVerticalWorkspaceAri,
	staticResults,
	verticalInstantiationStatus,
}: UseShowItamInput): UseShowItamResult => {
	const environment = useRelayEnvironment();
	const appPermissions = useApplicationPermissions();
	const appEditions = useAppEditions();
	const cloudId = useCloudId();

	const [hamResultsState, setHamResultsState] = useState<HamPermissionResultsState | null>(null);

	useEffect(() => {
		if (!cloudId || assetsVerticalWorkspaceAri == null) {
			return;
		}

		let isActive = true;
		const subscription = fetchItamHamWorkspacePermissions({
			environment,
			cloudId,
			assetsVerticalWorkspaceAri,
			onCompleted: (results) => {
				if (isActive) {
					setHamResultsState({ cloudId, assetsVerticalWorkspaceAri, results });
				}
			},
			onError: () => {
				if (isActive) {
					setHamResultsState({ cloudId, assetsVerticalWorkspaceAri, results: [] });
				}
			},
		});

		return () => {
			isActive = false;
			subscription.unsubscribe();
		};
	}, [environment, cloudId, assetsVerticalWorkspaceAri]);

	const hamResults =
		hamResultsState?.cloudId === cloudId &&
		hamResultsState.assetsVerticalWorkspaceAri === assetsVerticalWorkspaceAri
			? hamResultsState.results
			: [];
	const checks: HamPermissionChecksResult = buildHamPermissionChecksResult(
		cloudId ?? '',
		assetsVerticalWorkspaceAri,
		staticResults,
		hamResults,
	);

	const showItam = computeShouldShowItam(
		!!appPermissions?.hasServiceDeskAccess,
		appEditions?.serviceDesk,
		checks,
	);
	const hasHardwareSettingsPermission =
		showItam && (checks.permissions['manage-ham'] || checks.permissions['jira-admin']);

	return {
		showItam:
			showItam &&
			(checks.permissions['manage-ham'] ||
				checks.permissions['jira-admin'] ||
				checks.permissions['view-ham']),
		showHardwareSettings:
			hasHardwareSettingsPermission && verticalInstantiationStatus === 'COMPLETED',
	};
};
